import React, { useState, useEffect } from "react";
import { TrendingUp, Zap, RefreshCw, Download, Copy, CheckCircle } from "lucide-react";
import { tendersAPI } from "../services/api";
import toast from "react-hot-toast";

const REPORT_FOCUSES = [
  { id: "market_overview", label: "Market Overview Report", desc: "Full overview of the Nigerian procurement market" },
  { id: "sector_analysis", label: "Sector Deep Dive", desc: "In-depth analysis of a specific industry sector" },
  { id: "competitor_intelligence", label: "Competitor Intelligence", desc: "Who is winning contracts and why", elite: true },
  { id: "opportunity_forecast", label: "Opportunity Forecast", desc: "Upcoming tender opportunities in next 90 days" },
  { id: "win_strategy", label: "Win Strategy Report", desc: "Tactical advice to improve bid win rate" },
];

export default function MarketIntelligence() {
  const [analytics, setAnalytics] = useState(null);
  const [focus, setFocus] = useState("market_overview");
  const [sector, setSector] = useState("Oil & Gas");
  const [companyName, setCompanyName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => { tendersAPI.analytics().then(res => setAnalytics(res.data)); }, []);

  const generateReport = async () => {
    if (!analytics) { toast.error("Loading market data..."); return; }
    setGenerating(true);
    setReport("");

    try {
      await (async () => {
        const token = localStorage.getItem("access_token");
        const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
        const resp = await fetch(`${API}/ai/market-report`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ focus, sector, company_name: companyName }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          if (resp.status === 403) throw new Error(data.detail || "Plan upgrade required.");
          throw new Error(data.detail || "Generation failed");
        }
        setReport(data.report || "");
        toast.success("Report generated successfully!");
      })();
    } catch (err) { toast.error(err.message || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const download = () => {
    const blob = new Blob([report], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `market_intelligence_${focus}_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    toast.success("Report downloaded!");
  };

  const copy = () => { navigator.clipboard.writeText(report); setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 2000); };

  return (
    <div style={{ maxWidth: "960px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <div style={{ width: "44px", height: "44px", background: "rgba(0,212,161,0.1)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <TrendingUp size={22} color="var(--accent-green)" />
        </div>
        <div>
          <h1 style={{ fontSize: "24px" }}>Market Intelligence Reports</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "13px" }}>AI-powered reports using live ProTrackNG market data</p>
        </div>
      </div>

      {analytics && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "24px" }}>
          {[
            { label: "Live Market Value", value: analytics.summary?.total_market_value_formatted, color: "var(--accent-green)" },
            { label: "Active Tenders", value: analytics.summary?.open_tenders, color: "var(--accent-blue)" },
            { label: "Market Categories", value: analytics.by_category?.length, color: "var(--accent-orange)" },
          ].map(({ label, value, color }) => (
            <div key={label} className="card" style={{ textAlign: "center", padding: "16px", background: "linear-gradient(135deg, rgba(0,212,161,0.05), transparent)" }}>
              <div style={{ fontSize: "20px", fontFamily: "Syne", fontWeight: "800", color, marginBottom: "4px" }}>{value}</div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{label}</div>
              <div style={{ fontSize: "10px", color: "var(--accent-green)", marginTop: "2px" }}>? Live Data</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: "24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="card">
            <h3 style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "14px" }}>REPORT TYPE</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {REPORT_FOCUSES.map(f => (
                <button key={f.id} onClick={() => setFocus(f.id)} style={{ padding: "11px 14px", borderRadius: "10px", border: "1px solid", cursor: "pointer", textAlign: "left", transition: "all 0.15s", background: focus === f.id ? "var(--accent-green-dim)" : "var(--bg-secondary)", borderColor: focus === f.id ? "var(--accent-green)" : "var(--border)" }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: focus === f.id ? "var(--accent-green)" : "var(--text-primary)", marginBottom: "2px" }}>{f.label}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{f.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "14px" }}>SETTINGS</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Your Company Name</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Your Company Ltd" />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Target Sector</label>
                <select value={sector} onChange={e => setSector(e.target.value)}>
                  {["Oil & Gas", "Construction", "Healthcare", "ICT", "Agriculture", "Energy", "Security", "All Sectors"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
          <button className="btn btn-primary" onClick={generateReport} disabled={generating} style={{ width: "100%", justifyContent: "center", padding: "13px" }}>
            {generating ? <><RefreshCw size={15} style={{ animation: "spin 1s linear infinite" }} /> Generating...</> : <><Zap size={15} /> Generate Report</>}
          </button>
        </div>

        <div className="card" style={{ display: "flex", flexDirection: "column", minHeight: "600px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "15px" }}>Intelligence Report</h3>
            {report && (
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: "12px" }} onClick={copy}>
                  {copied ? <CheckCircle size={13} color="var(--accent-green)" /> : <Copy size={13} />} {copied ? "Copied!" : "Copy"}
                </button>
                <button className="btn btn-primary" style={{ padding: "6px 12px", fontSize: "12px" }} onClick={download}>
                  <Download size={13} /> Download
                </button>
              </div>
            )}
          </div>
          {!report && !generating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
              <TrendingUp size={48} style={{ marginBottom: "16px", opacity: 0.2 }} />
              <p style={{ fontSize: "14px", textAlign: "center" }}>Select report type and click Generate to get live market intelligence</p>
              <p style={{ fontSize: "12px", color: "var(--accent-green)", marginTop: "8px" }}>? Powered by live ProTrackNG data</p>
            </div>
          )}
          {generating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontSize: "40px", marginBottom: "16px" }}>??</div>
              <p style={{ color: "var(--accent-green)", fontSize: "14px", fontWeight: "600" }}>Analyzing live market data...</p>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", marginTop: "8px" }}>Building your intelligence report</p>
            </div>
          )}
          {report && (
            <div style={{ flex: 1, overflow: "auto" }}>
              <pre style={{ fontFamily: "DM Sans, sans-serif", fontSize: "12.5px", lineHeight: "1.9", color: "var(--text-primary)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{report}</pre>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
